// src/hooks/useChatWebSocket.ts
import { useState, useEffect, useMemo, useCallback } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { type } from "arktype";
import { ServerMessage, type DisplayMessage } from "../../types"; // Adjust path if needed

export function useChatWebSocket(roomId: string | null, enabled: boolean) {
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [chatError, setChatError] = useState<string | null>(null);
  const [subscriberCount, setSubscriberCount] = useState(0);

  const wsUrl = useMemo(() => {
    // ... (wsUrl logic remains the same)
    if (!enabled || !roomId) return null;
    const protocol = window.location.protocol === "http:" ? "ws:" : "wss:";
    const host = window.location.host;
    return `${protocol}//${host}/ws/${encodeURIComponent(roomId)}`;
  }, [enabled, roomId]);

  const {
    sendMessage: wsSendMessage,
    lastMessage,
    readyState,
  } = useWebSocket.default(wsUrl, {
    // ... (websocket options remain the same)
    shouldReconnect: (closeEvent) => true,
    onOpen: () => {
      console.log("WebSocket connected");
      setChatError(null);
    },
    onClose: () => console.log("WebSocket disconnected"),
    onError: (event) => {
      console.error("WebSocket error:", event);
      setChatError("WebSocket connection error. Trying to reconnect...");
    },
    retryOnError: true,
    reconnectAttempts: 10,
    reconnectInterval: 3000,
  });

  useEffect(() => {
    // ... (message receiving logic remains the same)
    if (lastMessage !== null) {
      try {
        const rawData = JSON.parse(lastMessage.data);
        const data = ServerMessage(rawData); // Validate with Arktype

        if (data instanceof type.errors) {
          console.error("Invalid message format:", data.summary);
          setChatError(`Received invalid message: ${data.summary}`);
          return;
        }

        if (data.type === "error") {
          setChatError(data.message ?? "Unknown server error");
        } else {
          const timestamp = new Date(data.timestamp);
          if (isNaN(timestamp.getTime())) {
            console.error("Invalid timestamp received:", data.timestamp);
            setChatError("Received message with invalid timestamp.");
            return; // Skip message with invalid date
          }
          setSubscriberCount(data.subscribers);
          setChatError(null); // Clear previous errors
          setMessages((prev) => [
            ...prev,
            {
              type: data.type,
              id: crypto.randomUUID(),
              // Assuming server now sends content like "username: message"
              // Or handle prepending based on message type if needed
              content: data.content,
              timestamp: timestamp,
              // You might want to add a 'sender' field from the server
              // to distinguish messages or style them differently.
            },
          ]);
        }
      } catch (error) {
        console.error("Failed to parse or validate WebSocket message:", error);
        setChatError("Received unparseable message from server.");
      }
    }
  }, [lastMessage]);

  // *** MODIFIED sendMessage ***
  // Now accepts username as the first argument
  const sendMessage = useCallback(
    (username: string, message: string) => {
      const trimmedMessage = message.trim();
      const trimmedUsername = username.trim() || "anon"; // Ensure username isn't empty

      if (trimmedMessage && readyState === ReadyState.OPEN) {
        // Prepend username to the message being sent
        const messageToSend = `${trimmedUsername}: ${trimmedMessage}`;
        wsSendMessage(messageToSend);
      }
    },
    [readyState, wsSendMessage] // Dependency array remains the same
  );

  const connectionStatus = useMemo(() => {
    // ... (connectionStatus logic remains the same)
    switch (readyState) {
      case ReadyState.CONNECTING:
        return "Connecting...";
      case ReadyState.OPEN:
        return `${subscriberCount} online`;
      case ReadyState.CLOSING:
        return "Closing...";
      case ReadyState.CLOSED:
        return "Disconnected";
      case ReadyState.UNINSTANTIATED:
        return "Idle";
      default:
        return "Unknown";
    }
  }, [readyState, subscriberCount]);

  return {
    messages,
    sendMessage, // Return the modified function
    connectionStatus,
    chatError,
    readyState,
  };
}
